import React, { useContext, useEffect, useState } from 'react';
import { Upload } from 'antd';
import { ThemeContext } from '../../../shared/contexts/Theme.context';
import CustomButton from '../../CustomButton/CustomButton';
import { notification } from 'antd';
import uploadDocumentRequest from '../../../services/uploadDocument.service';
import { BookingContext } from '../../../shared/contexts/Booking.context';
import { LanguageContext } from '../../../shared/contexts/Language.context';
import imageCompression from 'browser-image-compression';

const buttonContainerStyle = {
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  marginLeft: '-10%',
};

const SIZE_IN_MB = 20;

export default function UploadDocumentInput({
  field,
  currentValues,
  setCurrentFormValue,
  checkinGuestId,
}) {
  const { state: theme } = useContext(ThemeContext);
  const {
    state: { hotelId },
  } = useContext(BookingContext);
  const {
    state: { literals },
  } = useContext(LanguageContext);

  const [imageName, setImageName] = useState(currentValues[field.key]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!imageName) setImageName(currentValues[field.key]);
  }, [currentValues]);

  const onCustomRequest = async file => {
    const selectedFile = file.file;
    const isPNG = selectedFile.type === 'image/png';
    const isJPG = selectedFile.type === 'image/jpg' || selectedFile.type === 'image/jpeg';
    const isPDF = selectedFile.type === 'application/pdf';

    if (!isPNG && !isJPG && !isPDF) {
      notification.warning({
        message: `${selectedFile.name} ${literals['guestForm.additionalDocument.invalidFormat']} (png, jpg, pdf)`,
      });
      return false;
    }

    const isCorrectSize = selectedFile.size / 1024 / 1024 < SIZE_IN_MB;
    if (!isCorrectSize) {
      notification.warning({
        message: `${literals['guestForm.additionalDocument.invalidSize']} ${SIZE_IN_MB}MB`,
      });

      return false;
    }

    setLoading(true);

    try {
      let fileToUpload;
      if (isPDF) {
        fileToUpload = selectedFile;
      } else {
        fileToUpload = await compressImage(selectedFile);
      }
      setImageName(fileToUpload.name);
      uploadAdditionalDocument(fileToUpload);
    } catch (error) {
      setLoading(false);
      notification.error({
        message: literals['guestForm.additionalDocument.uploadError'],
      });
    }
  };

  const onChangeDocument = () => {
    setImageName(null);
    setCurrentFormValue(field.key, null);
  };

  const uploadAdditionalDocument = file => {
    uploadDocumentRequest(hotelId, {
      file,
      type: field.key,
      checkinGuestId: checkinGuestId,
    })
      .then(_res => {
        setLoading(false);
        setCurrentFormValue(field.key, file.name);
      })
      .catch(error => {
        setLoading(false);
        setImageName(null);
      });
  };

  const compressImage = async imageFile => {
    const options = {
      maxSizeMB: 2,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
    };

    try {
      const fileExtension = imageFile.name.split('.').pop();
      const lastDotIndex = imageFile.name.lastIndexOf('.');
      const baseName = lastDotIndex !== -1 ? imageFile.name.substring(0, lastDotIndex) : imageFile.name;
      const compressedFile = await imageCompression(imageFile, options);
      const newFileName = `${baseName}.${fileExtension}`;
      return new File([compressedFile], newFileName, { type: imageFile.type });
    } catch (error) {
      console.error('Error compressing image:', error);
      throw error;
    }
  };

  return (
    <>
      <div className="image-container">
        <p className={'title'}>
          {imageName ? imageName : literals['guestForm.additionalDocument.description']}
        </p>

        <Upload
          accept=".jpg,.jpeg,.png,.pdf"
          listType="text"
          customRequest={onCustomRequest}
          method="GET"
          disabled={loading}
        >
          <div style={buttonContainerStyle}>
            {imageName && !loading ? (
              <CustomButton
                text={literals['guestForm.additionalDocument.editButton']}
                type="text"
                small
                onClick={onChangeDocument}
              />
            ) : (
              <CustomButton
                text={literals['guestForm.additionalDocument.uploadButton']}
                type="text"
                small
                loading={loading}
              />
            )}
          </div>
        </Upload>
      </div>

      <style jsx>{`
        .image-container {
          width: 100%;
          margin-top: 10px;
          margin-bottom: 10px;
          text-align: center;
        }

        .image-container .upload-img {
          width: 100%;
          height: 150px;
          object-fit: contain;
        }

        .image-container .upload-img.placeholder {
          max-width: 50px;
        }

        .title {
          color: ${theme.titleColor};
          font-size: 14px;
          text-align: left;
          margin-right: 8px;
          margin-bottom: 14px;
          text-overflow: ellipsis;
          overflow: hidden;
        }
      `}</style>
      <style jsx global>{`
        .btn-container {
          display: flex;
          justify-content: center;
        }

        .ant-upload-list {
          display: none;
        }
      `}</style>
    </>
  );
}
